<template>
  <section class="usage-estimate">
    <component
      :is="collapsible ? 'SoLink' : 'div'"
      :icon="expanded ? 'chevron-up' : 'chevron-down'"
      block
      @click="expand()"
    >
      <slot name="prepend"></slot>
      <template v-if="isElectricity && hasTwoRateElectricity">
        <p data-testid="usage-estimate-eac-day">
          EAC (Day) {{ formatUsage(estimation.electricityDay) }}
        </p>
        <p data-testid="usage-estimate-eac-night">
          EAC (Night) {{ formatUsage(estimation.electricityNight) }}
        </p>
      </template>
      <p v-else data-testid="usage-estimate-eac">
        EAC
        {{
          formatUsage(isElectricity ? estimation.electricity : estimation.gas)
        }}
      </p>
    </component>
    <v-expand-transition>
      <div v-show="expanded">
        <SoDivider class="my-5" />
        <SoButtonGroup
          ref="quote-type-select"
          v-model="quoteType"
          :items="[QuoteType.Basic, QuoteType.Detailed]"
          class="mb-8"
          label="Quote Type"
        />
        <SoDivider class="mb-4" />
        <SoCheckbox
          v-if="isElectricity"
          ref="two-rate-checkbox"
          v-model="hasTwoRateElectricity"
          :disabled="isTwoRate !== null"
          label="2-rate electricity meter"
          class="mb-2"
        />
        <SoSlider
          v-if="hasTwoRateElectricity"
          ref="day-usage-slider"
          v-model="dayNightUsageRatio"
          :label="`Day usage: ${dayNightUsageRatio}%`"
          :min="0"
          :max="100"
          class="mb-4"
        />
        <SoButtonGroup
          v-if="isBasicQuote"
          ref="usage-intensity-select"
          v-model="usageIntensity"
          :items="[
            BasicUsageIntensity.Low,
            BasicUsageIntensity.Medium,
            BasicUsageIntensity.High,
          ]"
          class="mb-8"
          label="User"
        />
        <template v-else>
          <SoButtonGroup
            ref="bedroom-count-select"
            v-model="bedroomCount"
            :items="['1', '2', '3', '4', '5']"
            class="mb-4"
            label="How many bedrooms?"
          />
          <SoButtonGroup
            ref="cooking-fuel-select"
            v-model="cookingFuel"
            :items="[CookingFuel.Gas, CookingFuel.Electricity]"
            class="mb-4"
            label="Which fuel is used to cook?"
          />
          <SoButtonGroup
            ref="heating-fuel-select"
            v-model="heatingFuel"
            :items="[
              HeatingFuel.Gas,
              HeatingFuel.Electricity,
              HeatingFuel.Other,
            ]"
            class="mb-8"
            label="How do you heat your home?"
          />
        </template>
        <SoDivider class="mb-4" />
        <template v-if="isElectricity">
          <SoCheckbox
            ref="electric-vehicle-checkbox"
            v-model="hasElectricVehicle"
            label="Do you have an electric vehicle(s)?"
          />
          <UsageEstimatorElectricVehicle
            v-if="hasElectricVehicle"
            v-model="selectedElectricVehicles"
          />
          <SoCheckbox
            ref="solar-panel-checkbox"
            v-model="hasSolarPanels"
            label="Do you have solar panels?"
            class="mb-3"
          />
          <UsageEstimatorSolar
            v-if="hasSolarPanels"
            ref="solar-panel-selector"
            v-model="solarPanelConfiguration"
            class="mb-3"
          />
          <SoDivider class="mb-6" />
        </template>
        <UsageEstimatorOfgemBands :usage-band="ofgemCategoryOfEstimation" />
      </div>
    </v-expand-transition>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType, nextTick } from "vue"
  import SoSlider from "@/components/SoSlider.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoButtonGroup from "@/components/SoButtonGroup.vue"
  import SoLink from "@/components/SoLink.vue"
  import UsageEstimatorElectricVehicle from "./UsageEstimatorElectricVehicle.vue"
  import UsageEstimatorSolar from "./UsageEstimatorSolar.vue"
  import UsageEstimatorOfgemBands from "./UsageEstimatorOfgemBands.vue"
  import { UsageEstimationService } from "@/services/UsageEstimationService"
  import {
    QuoteType,
    CookingFuel,
    HeatingFuel,
    IElectricVehicleUsage,
    IUsageAdjustment,
    BasicUsageIntensity,
    ISolarPanelConfiguration,
  } from "@/services/UsageEstimationService/types"

  export default defineComponent({
    components: {
      SoCheckbox,
      SoDivider,
      SoSlider,
      SoButtonGroup,
      SoLink,
      UsageEstimatorElectricVehicle,
      UsageEstimatorSolar,
      UsageEstimatorOfgemBands,
    },
    props: {
      isElectricity: {
        type: Boolean,
        default: true,
      },
      isTwoRate: {
        type: [Boolean, null] as PropType<boolean | null>,
        default: null,
      },
      collapsible: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["updated"],
    data() {
      return {
        QuoteType,
        CookingFuel,
        HeatingFuel,
        BasicUsageIntensity,
        quoteType: QuoteType.Detailed,
        usageIntensity: BasicUsageIntensity.Medium,
        cookingFuel: CookingFuel.Gas,
        heatingFuel: HeatingFuel.Electricity,
        bedroomCount: "2",
        hasElectricVehicle: false,
        hasSolarPanels: false,
        hasTwoRateElectricity: false,
        dayNightUsageRatio: 58,
        solarPanelConfiguration: {
          panelCount: 0,
          panel: null,
        } as ISolarPanelConfiguration,
        selectedElectricVehicles: [
          {
            milesPerYear: 0,
            vehicle: null,
          },
        ] as IElectricVehicleUsage[],
        expanded: true,
      }
    },
    computed: {
      ofgemCategoryOfEstimation() {
        return UsageEstimationService.getOfgemBandForEstimation(
          this.estimation,
          {
            isElectricity: this.isElectricity,
            hasTwoRateElectricity: this.hasTwoRateElectricity,
          },
        ) as BasicUsageIntensity
      },
      isBasicQuote() {
        return this.quoteType === QuoteType.Basic
      },
      extraUsageDetails() {
        const details: IUsageAdjustment = {}

        if (this.hasTwoRateElectricity) {
          details.dayNightUsageRatio = this.dayNightUsageRatio
        }

        if (this.hasSolarPanels && this.solarPanelConfiguration.panel) {
          details.solarPanelConfiguration = {
            panel: this.solarPanelConfiguration.panel,
            panelCount: this.solarPanelConfiguration.panelCount,
          }
        }

        if (this.hasElectricVehicle) {
          details.electricVehicles = this.selectedElectricVehicles
        }

        return details
      },
      estimation() {
        return this.isBasicQuote
          ? UsageEstimationService.getBasicUsageEstimation(
              this.usageIntensity,
              this.extraUsageDetails,
            )
          : UsageEstimationService.getDetailedUsageEstimation(
              this.cookingFuel,
              this.heatingFuel,
              Number(this.bedroomCount),
              this.extraUsageDetails,
            )
      },
    },
    watch: {
      estimation(val) {
        this.$emit("updated", val)
      },
    },
    mounted() {
      if (this.collapsible) {
        this.expanded = false
      }
      if (this.isTwoRate) {
        this.hasTwoRateElectricity = true
      }
      nextTick(() => {
        this.$emit("updated", this.estimation)
      })
    },
    methods: {
      formatUsage(usage) {
        const roundedWithCommas = Math.round(usage).toLocaleString()
        return `${roundedWithCommas} kWh`
      },
      expand() {
        if (this.collapsible) {
          this.expanded = !this.expanded
        }
      },
    },
  })
</script>
